<template>
  <div class="step--navigation">
    <div class="step--navigation-container" :class="{'show-continue': showContinue}">
      <v-layout>
        <v-flex hidden-sm-and-down>
          <BackButton v-if="showBack" @click="clickBack" />
        </v-flex>

        <v-flex class="continue">
          <v-layout row wrap>
            <v-flex class="text-md-right">
              <v-btn
                v-if="showSummaryButton"
                :block="$vuetify.breakpoint.smAndDown"
                color="orange"
                flat
                id="review-appointment-button"
                type="button"
                @click="clickReviewAppointment">
                Review Appointment
              </v-btn>

              <ButtonCommon
                v-if="showContinue"
                :block="$vuetify.breakpoint.smAndDown"
                :disabled="continueDisabled"
                :type="submitMode ? 'submit' : 'button'"
                id="continue-button"
                @click="clickContinue">
                {{continueText}}
              </ButtonCommon>

              <BookAppointmentButton v-if="showBookAppointment" block submit-mode />
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import BackButton from '@/components/common/BackButton.vue';
import BookAppointmentButton from '@/components/BookAppointmentButton.vue';
import ButtonCommon from '@/components/common/ButtonCommon.vue';
import RouteNames from '@/router/constants/route-names';
import StepUtilities from '@/router/utilities/step-utilities';

export default {
  components: {
    BackButton,
    BookAppointmentButton,
    ButtonCommon,
  },
  props: {
    hideBack: {
      type: Boolean,
      default: false,
    },
    hideContinue: {
      type: Boolean,
      default: false,
    },
    continueDisabled: {
      type: Boolean,
      default: false,
    },
    showBookAppointment: {
      type: Boolean,
      default: false,
    },
    submitMode: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('Appointment', [
      'hasTime',
      'hasLocation',
      'reachedSummary',
    ]),
    ...mapState('Auth', [
      'user',
    ]),
    continueText() {
      const signinStepIndex = StepUtilities.getStepIndexByRouteName(RouteNames.AppointmentSignIn);
      const createAccountStepIndex = StepUtilities
        .getStepIndexByRouteName(RouteNames.AppointmentCreateAccount);
      const contactStepIndex = StepUtilities.getStepIndexByRouteName(RouteNames.AppointmentContact);
      const currentStepIndex = StepUtilities.getStepIndexByRouteName(this.$route.name);

      switch (currentStepIndex) {
        case signinStepIndex:
          return 'Sign In';
        case createAccountStepIndex:
          return 'Sign Up';
        case contactStepIndex:
          return 'Continue';
        default:
          return 'Continue';
      }
    },
    currentRouteIndex() {
      return StepUtilities.getStepIndexByRouteName(this.$route.name);
    },
    showBack() {
      return this.currentRouteIndex > 0
        && this.currentRouteIndex <= StepUtilities.stepOrder.length
        && !this.hideBack;
    },
    showContinue() {
      return this.currentRouteIndex < StepUtilities.stepOrder.length
        && !this.hideContinue
        && !this.showBookAppointment;
    },
    showSummaryButton() {
      const apptHasData = this.hasTime && this.hasLocation;
      const isAuthenticated = this.user && this.user.access_token;

      const contactStepIndex = StepUtilities.getStepIndexByRouteName(RouteNames.AppointmentContact);
      const currentStepIndex = StepUtilities.getStepIndexByRouteName(this.$route.name);
      const notOnFinalStep = currentStepIndex < contactStepIndex;

      return !!(apptHasData && notOnFinalStep && this.reachedSummary && isAuthenticated);
    },
  },
  methods: {
    async clickBack() {
      if (this.continueDisabled) {
        this.$store.commit('Appointment/setReachedSummary', false);
      }
      this.$emit('save');
      await StepUtilities.goToPreviousStep(this.$route, this.$router);
    },
    clickContinue() {
      if (this.submitMode) {
        return;
      }

      this.$emit('save');
      StepUtilities.goToNextStep(this.$route, this.$router);
    },
    clickReviewAppointment() {
      if (!this.continueDisabled) {
        this.$emit('save');
      }
      StepUtilities.goToSummaryStep(this.$router);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/mixins/breakpoints';
@import '../styles/vars/theme';
@import '../styles/vars/typography';

.step--navigation {
  padding: 30px 0 0;

  &-container {
    border-top: 1px solid $theme-color-gray-light;
    padding: 28px 0 14px;
  }

  .button--book-appointment {
    display: none;
  }
}

.continue {
  display: flex;
  justify-content: flex-end;
}

.continue-button {
  color: $theme-color-black;
}

:disabled {
  color: $theme-color-gray-light;
}

@include responsive-breakpoint(sm) {
  .step--navigation {
    background: $theme-color-white;
    bottom: 0;
    box-shadow: 0 -1px 6px rgba(0, 0, 0, 0.15);
    left: 0;
    padding: 10px 16px;
    position: fixed;
    width: 100%;
    z-index: 4;

    &-container {
      border: none;
      padding: 5px 0;
    }

    .button--book-appointment {
      display: block;
    }

    #review-appointment-button {
      display: none;
    }
  }
}
</style>
